import _ from "lodash";
import { formValueSelector } from "redux-form";

import { TIERED_CONTRIBUTION_ROLES, MIN_TIERED_CONTRIBUTIONS, CONTRIBUTION_TYPES } from "Common/Constants";

import type { State } from "EmployerApplication/createStore";

const selector = formValueSelector("employerApplicationForm");

export const getTieredContributions = (state) => {
  const contributions = state.entities.companyContributions;
  if (contributions === undefined || (contributions && contributions.length === 0)) {
    return [];
  }
  return _.values(contributions).filter((contribution) => contribution.role !== null);
};

// TODO Hammad this selector is no longer tiered contributions specific.
// 1. Move it to the EmployerApplication/Selectors.
// 2. Implement it all (3) other places we use "company_policy[type_of_contribution]"
export const getContributionType = (state: State) => selector(state, "company_policy[type_of_contribution]");

const hasValue = (fieldValue) => fieldValue !== undefined && fieldValue !== null && fieldValue !== "";

// Given a contribution tier from a form, determine whether it has complete data
export const contributionIsComplete = (contribution, contributionType) => {
  if (!hasValue(contribution.role)) {
    return false;
  }

  if ([CONTRIBUTION_TYPES.percentage, CONTRIBUTION_TYPES.percent_of_base_plan].includes(contributionType)) {
    return hasValue(contribution.employeePercentage) && hasValue(contribution.dependentPercentage);
  }
  return hasValue(contribution.employeeFixedAmount) && hasValue(contribution.dependentFixedAmount);
};

// Construct a new tierOptions object for the role dropdown in each row of the form.
// Exclude any roles that have been chosen already for a different row to keep tiers unique.
// For example, if there are two rows and the only options are owners and managers, but
// managers was chosen on the first row, the arrayOfTierOptions would look like this:
// [
//   {
//     "Managers": "managers", <- selected
//     "Owners": "owners",
//   },
//   {
//     "Owners": "owners"
//   }
// ]
export const constructArrayOfTierOptions = (state) => {
  const tieredContributionsFormValues = selector(state, "tiered_contributions");
  const tierOptions = _.invert(TIERED_CONTRIBUTION_ROLES);
  let selectedRoles = [];

  if (tieredContributionsFormValues !== undefined) {
    selectedRoles = tieredContributionsFormValues.map((contribution) => contribution.role);
  }

  const arrayOfTierOptions = selectedRoles.map((selectedRoleForRow) => {
    const tierOptionsAsArray = Object.entries(tierOptions);
    // remove all roles that have been selected on other rows - leaving in the role selected on the current row
    const filteredTierOptions = tierOptionsAsArray.filter(
      ([_roleDisplay, roleValue]) => roleValue === selectedRoleForRow || !selectedRoles.includes(roleValue),
    );

    return _.fromPairs(filteredTierOptions);
  });

  return arrayOfTierOptions;
};

// Count the rows where all fields in that row have been filled out
export const getCompletedRowCount = (state) => {
  const tieredContributionsFormValues = selector(state, "tiered_contributions");
  const contributionType = getContributionType(state);
  let completedRowCount = 0;

  if (tieredContributionsFormValues !== undefined) {
    tieredContributionsFormValues.forEach((contribution) => {
      if (contributionIsComplete(contribution, contributionType)) {
        completedRowCount += 1;
      }
    });
  }
  return completedRowCount;
};

export const tieredContributionInitialValues = (state) => {
  const initialTieredContributions = getTieredContributions(state);

  const defaultContribution = {
    role: "full_time",
    employeePercentage: "50",
    dependentPercentage: "0",
    employeeFixedAmount: null,
    dependentFixedAmount: null,
  };

  const emptyContribution = {
    role: null,
    employeePercentage: null,
    dependentPercentage: null,
    employeeFixedAmount: null,
    dependentFixedAmount: null,
  };

  // Set first row to default contribution if no contributions exist yet
  if (initialTieredContributions.length === 0) {
    initialTieredContributions.push(defaultContribution);
  }

  // Initialize form with additional empty rows if no contributions exist yet
  while (initialTieredContributions.length < MIN_TIERED_CONTRIBUTIONS) {
    initialTieredContributions.push(emptyContribution);
  }

  return initialTieredContributions;
};

export const hasPartTimeContribution = (state) => {
  const initialTieredContributions = getTieredContributions(state);
  return initialTieredContributions.some((contribution) => contribution.role === "part_time");
};
